import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Menu, X, Phone, Mail, Globe, Hash, AtSign, Share2,
  MapPin, Clock, Users, Award, ChevronLeft, ChevronRight,
  CheckCircle, Calendar, Star, Send, ExternalLink, PlayCircle,
  Layers, Shield, Zap
} from 'lucide-react';
import { getPortalConfig, savePortalConfig, getPreviewConfig, clearPreviewConfig, ACADEMY_THEMES } from '../constants/academyThemes';

// ─── Kynaix Sports default theme (used when branding=default) ─────────────────

const KYNAIX_SPORTS_THEME = {
  id: 'kynaix-default',
  primary: '#2563eb',
  primaryDark: '#1d4ed8',
  primaryLight: '#eff6ff',
  textOnPrimary: '#ffffff'
};

// ─── Design Tokens ────────────────────────────────────────────────────────────

const FONT_PAIRINGS = {
  modern: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  classic: "Georgia, Cambria, 'Times New Roman', serif",
  sporty: "'Arial Black', 'Helvetica Neue', Arial, sans-serif"
};

function getCardStyles(cardStyle, theme) {
  if (cardStyle === 'bordered') {
    return { backgroundColor: '#ffffff', border: `2px solid ${theme.primary}`, boxShadow: 'none', borderRadius: '1rem', overflow: 'hidden' };
  }
  if (cardStyle === 'flat') {
    return { backgroundColor: theme.primaryLight, border: 'none', boxShadow: 'none', borderRadius: '1rem', overflow: 'hidden' };
  }
  return { backgroundColor: '#ffffff', border: '1px solid #f3f4f6', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderRadius: '1rem', overflow: 'hidden' };
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_COACHES = [
  {
    id: 1,
    name: 'Sarah Johnson',
    title: 'Head Coach',
    specializations: ['Tennis', 'Pickleball'],
    bio: 'Former WTA professional with over 15 years of coaching experience. Sarah has trained national-level juniors and adult recreational players alike, bringing a personalized approach to every lesson.',
    image: 'https://i.pravatar.cc/300?img=1',
    certifications: ['PTR Certified', 'USPTA Professional']
  },
  {
    id: 2,
    name: 'Mike Chen',
    title: 'Senior Coach',
    specializations: ['Tennis'],
    bio: 'NCAA Division I standout and competitive circuit veteran. Mike specializes in match strategy and mental game development for intermediate to advanced players.',
    image: 'https://i.pravatar.cc/300?img=2',
    certifications: ['USPTA Elite Professional']
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    title: 'Junior Program Director',
    specializations: ['Tennis', 'Basketball'],
    bio: 'Passionate about youth development, Emily runs our junior programs from beginner to high-performance. She holds advanced certifications in athletic development for players aged 6–18.',
    image: 'https://i.pravatar.cc/300?img=5',
    certifications: ['USTA High Performance Coach', 'Youth Specialist']
  },
  {
    id: 4,
    name: 'David Park',
    title: 'Fitness & Conditioning Coach',
    specializations: ['Strength & Conditioning', 'Tennis'],
    bio: 'Former collegiate athlete turned strength and conditioning specialist. David designs on-court fitness programs that translate directly to match performance.',
    image: 'https://i.pravatar.cc/300?img=8',
    certifications: ['NSCA-CSCS', 'ACE Certified']
  }
];

const MOCK_CLASSES = [
  {
    id: 1,
    className: 'Adult Beginner Tennis',
    description: 'Perfect for adults new to tennis. Learn basic strokes and court positioning in a fun, relaxed group setting.',
    skillLevel: 'beginner',
    coachName: 'Sarah Johnson',
    recurrence: 'Mon / Wed / Fri',
    time: '6:00 PM',
    duration: 90,
    enrolled: 15,
    maxCapacity: 20,
    facility: 'Court A',
    enrollmentFee: 150,
    registrationOpen: true
  },
  {
    id: 2,
    className: 'Intermediate Tennis',
    description: 'Improve your game with advanced technique drills, point play, and match strategy. Recommended for players with 1+ year experience.',
    skillLevel: 'intermediate',
    coachName: 'Mike Chen',
    recurrence: 'Tue / Thu',
    time: '5:00 PM',
    duration: 90,
    enrolled: 12,
    maxCapacity: 16,
    facility: 'Court B',
    enrollmentFee: 180,
    registrationOpen: true
  },
  {
    id: 3,
    className: 'Junior High-Performance',
    description: 'Elite junior training for competitive players ages 12–18. Focuses on tournament preparation, fitness, and mental toughness.',
    skillLevel: 'advanced',
    coachName: 'Emily Rodriguez',
    recurrence: 'Mon / Wed / Fri',
    time: '4:00 PM',
    duration: 120,
    enrolled: 10,
    maxCapacity: 12,
    facility: 'Court A & B',
    enrollmentFee: 250,
    registrationOpen: false
  },
  {
    id: 4,
    className: 'Weekend Cardio Tennis',
    description: 'High-energy group workout combining tennis drills with cardio exercises. Great for fitness-focused players.',
    skillLevel: 'all',
    coachName: 'David Park',
    recurrence: 'Saturday',
    time: '9:00 AM',
    duration: 60,
    enrolled: 18,
    maxCapacity: 25,
    facility: 'Indoor Court',
    enrollmentFee: 80,
    registrationOpen: true
  }
];

const MOCK_COURTS = [
  {
    id: 1,
    name: 'Court A',
    surface: 'Clay',
    status: 'available',
    hourlyRate: 25,
    image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=600',
    features: ['LED Lighting', 'Net Camera', 'Seating for 20']
  },
  {
    id: 2,
    name: 'Court B',
    surface: 'Hard',
    status: 'in-use',
    hourlyRate: 20,
    image: 'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?w=600',
    features: ['Practice Wall', 'Ball Machine Available']
  },
  {
    id: 3,
    name: 'Indoor Court',
    surface: 'Carpet',
    status: 'available',
    hourlyRate: 35,
    image: 'https://images.unsplash.com/photo-1519766304817-4f37bba85e1c?w=600',
    features: ['Climate Controlled', 'Professional Lighting', 'Video Analysis Setup']
  }
];

const GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400',
  'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?w=400',
  'https://images.unsplash.com/photo-1464983953574-0892a716854b?w=400',
  'https://images.unsplash.com/photo-1519766304817-4f37bba85e1c?w=400',
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400',
  'https://images.unsplash.com/photo-1530915534664-4ac6423a5b4a?w=400',
  'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400',
  'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=400',
  'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400',
  'https://images.unsplash.com/photo-1599058917765-a780eda07a3e?w=400',
  'https://images.unsplash.com/photo-1607962837359-5e7e89f86776?w=400',
  'https://images.unsplash.com/photo-1540747913346-19212a4cf528?w=400'
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name) {
  return name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function skillBadgeColorClass(level) {
  const map = {
    beginner: 'bg-green-600',
    intermediate: 'bg-amber-600',
    advanced: 'bg-red-600',
    all: 'bg-violet-700'
  };
  return map[level] || 'bg-gray-500';
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ─── PortalNav ─────────────────────────────────────────────────────────────────

function PortalNav({ config, theme, activeTab, setActiveTab, onEnroll, mobileMenuOpen, setMobileMenuOpen, isDefaultBranding = false, withinPlatform = false }) {
  const navTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'faculty', label: 'Faculty' },
    { id: 'classes', label: 'Classes' },
    { id: 'courts', label: 'Courts & Fields' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <nav
      className="sticky shadow-md"
      style={{ backgroundColor: theme.primary, top: withinPlatform ? 56 : 0, zIndex: withinPlatform ? 40 : 50 }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Name */}
          {isDefaultBranding ? (
            <div className="flex items-center gap-3 flex-shrink-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff', letterSpacing: '-0.02em' }}
              >
                KS
              </div>
              <div className="flex-col leading-tight hidden sm:flex">
                <span className="text-white font-black text-base">Kynaix Sports</span>
                <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{config.academyName}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-shrink-0">
              {config.logo ? (
                <img src={config.logo} alt="logo" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#ffffff' }}
                >
                  {getInitials(config.academyName)}
                </div>
              )}
              <span className="text-white font-bold text-lg leading-tight hidden sm:block">
                {config.academyName}
              </span>
            </div>
          )}

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-3 py-2 rounded text-sm font-medium transition-all"
                style={{
                  color: '#ffffff',
                  backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : 'transparent',
                  borderBottom: activeTab === tab.id ? '2px solid #ffffff' : '2px solid transparent'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={onEnroll}
              className="hidden sm:block px-4 py-2 rounded-lg font-semibold text-sm transition-all hover:opacity-90"
              style={{ backgroundColor: '#ffffff', color: theme.primary }}
            >
              Enroll Now
            </button>
            <button
              className="lg:hidden p-2 rounded text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t" style={{ borderColor: 'rgba(255,255,255,0.2)', backgroundColor: theme.primary }}>
          <div className="px-4 py-3 space-y-1">
            {navTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                className="w-full text-left px-3 py-2 rounded text-sm font-medium"
                style={{
                  color: '#ffffff',
                  backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : 'transparent'
                }}
              >
                {tab.label}
              </button>
            ))}
            <button
              onClick={() => { onEnroll(); setMobileMenuOpen(false); }}
              className="w-full mt-2 px-4 py-2 rounded-lg font-semibold text-sm"
              style={{ backgroundColor: '#ffffff', color: theme.primary }}
            >
              Enroll Now
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── PortalFooter ─────────────────────────────────────────────────────────────

function PortalFooter({ config, theme, setActiveTab, isDefaultBranding = false }) {
  return (
    <footer style={{ backgroundColor: theme.primaryDark }}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            {isDefaultBranding ? (
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff', letterSpacing: '-0.02em' }}
                >
                  KS
                </div>
                <div>
                  <div className="text-white font-black text-lg leading-tight">Kynaix Sports</div>
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{config.academyName}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
                >
                  {getInitials(config.academyName)}
                </div>
                <span className="text-white font-bold text-lg">{config.academyName}</span>
              </div>
            )}
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{config.tagline}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-3">Quick Links</h4>
            <div className="space-y-2">
              {['overview', 'classes', 'contact'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="block text-sm capitalize hover:text-white transition-colors"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-white font-semibold mb-3">Follow Us</h4>
            <div className="flex gap-3">
              {config.contact?.instagram && (
                <a href={`https://instagram.com/${config.contact.instagram}`} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
                >
                  <Hash size={16} />
                </a>
              )}
              {config.contact?.facebook && (
                <a href={`https://facebook.com/${config.contact.facebook}`} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
                >
                  <Share2 size={16} />
                </a>
              )}
              {config.contact?.twitter && (
                <a href={`https://twitter.com/${config.contact.twitter}`} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
                >
                  <AtSign size={16} />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Powered by <span style={{ color: 'rgba(255,255,255,0.6)' }}>Kynaix Sports</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

// ─── OverviewSection ───────────────────────────────────────────────────────────

function OverviewSection({ config, theme, setActiveTab, onEnroll, navigate, heroStyle = 'fullscreen', cardStyle = 'elevated' }) {
  const stats = [
    { label: 'Members', value: '45+' },
    { label: 'Coaches', value: '6' },
    { label: 'Classes', value: '8' },
    { label: 'Courts', value: '4' }
  ];

  return (
    <div>
      {/* Hero — 3 layout variants */}
      {heroStyle === 'split' ? (
        <div className="flex flex-col md:flex-row" style={{ minHeight: 480 }}>
          <div className="flex-1 flex items-center px-8 py-16 md:py-0" style={{ backgroundColor: theme.primary }}>
            <div className="text-white max-w-lg">
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Welcome to
              </p>
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">{config.academyName}</h1>
              <p className="text-lg md:text-xl mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>{config.tagline}</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={onEnroll} className="px-6 py-3 rounded-lg font-bold text-base transition-all hover:opacity-90" style={{ backgroundColor: '#ffffff', color: theme.primary }}>
                  Enroll Now
                </button>
                <button onClick={() => setActiveTab('classes')} className="px-6 py-3 rounded-lg font-bold text-base border-2 border-white text-white transition-all hover:bg-white/10">
                  Browse Classes
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 relative" style={{ minHeight: 300 }}>
            <img
              src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=900"
              alt={config.academyName}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>
      ) : heroStyle === 'minimal' ? (
        <div style={{ backgroundColor: theme.primary }} className="py-24 px-6 text-center">
          <div className="max-w-3xl mx-auto text-white">
            <div
              className="inline-flex w-16 h-16 rounded-2xl items-center justify-center font-bold text-2xl mb-6"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
            >
              {getInitials(config.academyName)}
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-4 leading-tight">{config.academyName}</h1>
            <p className="text-xl mb-10" style={{ color: 'rgba(255,255,255,0.8)' }}>{config.tagline}</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button onClick={onEnroll} className="px-10 py-3.5 rounded-xl font-bold text-lg transition-all hover:opacity-90 shadow-lg" style={{ backgroundColor: '#ffffff', color: theme.primary }}>
                Enroll Now
              </button>
              <button onClick={() => setActiveTab('classes')} className="px-10 py-3.5 rounded-xl font-bold text-lg border-2 border-white text-white transition-all hover:bg-white/10">
                Browse Classes
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* fullscreen (default) */
        <div
          className="relative min-h-[520px] flex items-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=1600)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0" style={{ background: `linear-gradient(rgba(0,0,0,0.35), ${theme.primary}cc)` }} />
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 text-white">
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Welcome to
            </p>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-4 leading-tight">{config.academyName}</h1>
            <p className="text-xl md:text-2xl mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>{config.tagline}</p>
            <div className="flex flex-wrap gap-4">
              <button onClick={onEnroll} className="px-8 py-3 rounded-lg font-bold text-lg transition-all hover:opacity-90" style={{ backgroundColor: '#ffffff', color: theme.primary }}>
                Enroll Now
              </button>
              <button onClick={() => setActiveTab('classes')} className="px-8 py-3 rounded-lg font-bold text-lg border-2 border-white text-white transition-all hover:bg-white/10">
                Browse Classes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar (shown for fullscreen + split) */}
      {heroStyle !== 'minimal' && (
      <div
        className="py-8"
        style={{ backgroundColor: theme.primaryDark }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            {stats.map(s => (
              <div key={s.label}>
                <div className="text-3xl font-extrabold">{s.value}</div>
                <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* About */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">About {config.academyName}</h2>
            <div className="w-12 h-1 rounded mb-6" style={{ backgroundColor: theme.primary }} />
            <p className="text-gray-600 text-lg leading-relaxed">{config.about}</p>
            <button
              onClick={() => setActiveTab('contact')}
              className="mt-8 inline-flex items-center gap-2 font-semibold text-sm"
              style={{ color: theme.primary }}
            >
              Get in touch <ExternalLink size={14} />
            </button>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl h-72">
            <img
              src="https://images.unsplash.com/photo-1464983953574-0892a716854b?w=700"
              alt="Academy training"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Featured Classes */}
      <div className="py-16" style={{ backgroundColor: theme.primaryLight }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Featured Classes</h2>
              <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
            </div>
            <button
              onClick={() => setActiveTab('classes')}
              className="text-sm font-semibold hover:underline"
              style={{ color: theme.primary }}
            >
              View all classes →
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {MOCK_CLASSES.slice(0, 3).map(cls => (
              <div key={cls.id} style={getCardStyles(cardStyle, theme)}>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold text-white capitalize ${skillBadgeColorClass(cls.skillLevel)}`}
                    >
                      {cls.skillLevel}
                    </span>
                    {cls.registrationOpen ? (
                      <span className="text-xs text-green-600 font-semibold bg-green-50 px-2 py-0.5 rounded-full">Open</span>
                    ) : (
                      <span className="text-xs text-red-600 font-semibold bg-red-50 px-2 py-0.5 rounded-full">Closed</span>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{cls.className}</h3>
                  <p className="text-gray-500 text-sm mb-4 line-clamp-2">{cls.description}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Clock size={14} /> {cls.recurrence} · {cls.time}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Users size={14} /> {cls.enrolled}/{cls.maxCapacity} enrolled
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg" style={{ color: theme.primary }}>
                      {cls.enrollmentFee > 0 ? `$${cls.enrollmentFee}` : 'Free'}
                    </span>
                    <button
                      onClick={() => navigate(`/register/class/${cls.id}`)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                      style={{ backgroundColor: theme.primary }}
                    >
                      Enroll
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Meet Coaches Preview */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Meet Our Coaches</h2>
            <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
          </div>
          <button
            onClick={() => setActiveTab('faculty')}
            className="text-sm font-semibold hover:underline"
            style={{ color: theme.primary }}
          >
            View all coaches →
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {MOCK_COACHES.slice(0, 3).map(coach => (
            <div key={coach.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 text-center">
              <img
                src={coach.image}
                alt={coach.name}
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
              />
              <h3 className="font-bold text-gray-900">{coach.name}</h3>
              <p className="text-sm mb-3" style={{ color: theme.primary }}>{coach.title}</p>
              <div className="flex flex-wrap gap-1 justify-center">
                {coach.specializations.map(s => (
                  <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── FacultySection ────────────────────────────────────────────────────────────

function FacultySection({ theme }) {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-gray-900">Our Faculty</h2>
        <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
        <p className="text-gray-500 mt-3">Meet the expert coaches who will guide your journey.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {MOCK_COACHES.map(coach => (
          <div key={coach.id} className="bg-white border border-gray-100 rounded-2xl shadow-md overflow-hidden flex flex-col sm:flex-row">
            <img
              src={coach.image}
              alt={coach.name}
              className="w-full sm:w-40 h-48 sm:h-auto object-cover flex-shrink-0"
            />
            <div className="p-6 flex flex-col justify-between">
              <div>
                <h3 className="font-bold text-xl text-gray-900">{coach.name}</h3>
                <p className="font-semibold text-sm mb-3" style={{ color: theme.primary }}>{coach.title}</p>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{coach.bio}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {coach.specializations.map(s => (
                    <span key={s}
                      className="text-xs px-2.5 py-0.5 rounded-full text-white font-medium"
                      style={{ backgroundColor: theme.primary }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  {coach.certifications.map(c => (
                    <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <button
                className="mt-4 w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all"
                style={{ borderColor: theme.primary, color: theme.primary }}
              >
                Contact Coach
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ClassesSection ────────────────────────────────────────────────────────────

function ClassesSection({ theme, navigate, cardStyle = 'elevated' }) {
  const [filterLevel, setFilterLevel] = useState('all');
  const levels = ['all', 'beginner', 'intermediate', 'advanced'];

  const filtered = filterLevel === 'all'
    ? MOCK_CLASSES
    : MOCK_CLASSES.filter(c => c.skillLevel === filterLevel);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Classes & Programs</h2>
        <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-8 flex-wrap">
        {levels.map(level => (
          <button
            key={level}
            onClick={() => setFilterLevel(level)}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all ${
              filterLevel === level ? 'text-white' : 'bg-gray-100 text-gray-700'
            }`}
            style={filterLevel === level ? { backgroundColor: theme.primary } : undefined}
          >
            {level === 'all' ? 'All Levels' : level}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map(cls => (
          <div key={cls.id} style={getCardStyles(cardStyle, theme)}>
            <div
              className={`h-2 ${skillBadgeColorClass(cls.skillLevel)}`}
            />
            <div className="p-6">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-xl text-gray-900">{cls.className}</h3>
                {cls.registrationOpen ? (
                  <span className="text-xs text-green-600 font-bold bg-green-50 px-3 py-1 rounded-full ml-2 flex-shrink-0">
                    Open
                  </span>
                ) : (
                  <span className="text-xs text-red-600 font-bold bg-red-50 px-3 py-1 rounded-full ml-2 flex-shrink-0">
                    Closed
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mb-4 leading-relaxed">{cls.description}</p>
              <div className="grid grid-cols-2 gap-3 mb-5 text-sm text-gray-600">
                <div className="flex items-center gap-2"><Clock size={14} /> {cls.recurrence}</div>
                <div className="flex items-center gap-2"><Clock size={14} /> {cls.time} · {cls.duration}min</div>
                <div className="flex items-center gap-2"><Users size={14} /> {cls.enrolled}/{cls.maxCapacity}</div>
                <div className="flex items-center gap-2"><MapPin size={14} /> {cls.facility}</div>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-2xl font-extrabold" style={{ color: theme.primary }}>
                    {cls.enrollmentFee > 0 ? `$${cls.enrollmentFee}` : 'Free'}
                  </div>
                  <div className="text-xs text-gray-400">Coach: {cls.coachName}</div>
                </div>
                <button
                  onClick={() => navigate(`/register/class/${cls.id}`)}
                  className="px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: theme.primary }}
                >
                  Enroll Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CourtsSection ─────────────────────────────────────────────────────────────

function CourtsSection({ theme }) {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-gray-900">Courts & Facilities</h2>
        <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
        <p className="text-gray-500 mt-3">World-class facilities to support your training and play.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {MOCK_COURTS.map(court => (
          <div key={court.id} className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="relative h-48">
              <img src={court.image} alt={court.name} className="w-full h-full object-cover" />
              <span
                className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold text-white ${court.status === 'available' ? 'bg-green-600' : 'bg-orange-600'}`}
              >
                {court.status === 'available' ? 'Available' : 'In Use'}
              </span>
            </div>
            <div className="p-5">
              <h3 className="font-bold text-xl text-gray-900">{court.name}</h3>
              <p className="text-sm mb-3" style={{ color: theme.primary }}>{court.surface} Surface</p>
              <ul className="space-y-1 mb-4">
                {court.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle size={13} style={{ color: theme.primary }} /> {f}
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg" style={{ color: theme.primary }}>${court.hourlyRate}/hr</span>
                <button
                  onClick={() => toast.success(`Booking request sent for ${court.name}!`)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: theme.primary }}
                >
                  Book Court
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ScheduleSection ──────────────────────────────────────────────────────────

function ScheduleSection({ theme }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Generate class occurrence dots (Mon/Wed/Fri = 1,3,5; Tue/Thu = 2,4)
  const classDots = {};
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month, d).getDay(); // 0=Sun
    classDots[d] = [];
    if ([1, 3, 5].includes(dow)) {
      classDots[d].push({ colorClass: 'bg-green-600', label: 'Adult Beginner' });
      classDots[d].push({ colorClass: 'bg-red-600', label: 'Junior HP' });
    }
    if ([2, 4].includes(dow)) {
      classDots[d].push({ colorClass: 'bg-amber-600', label: 'Intermediate' });
    }
    if (dow === 6) {
      classDots[d].push({ colorClass: 'bg-violet-600', label: 'Cardio Tennis' });
    }
  }

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-gray-900">Class Schedule</h2>
        <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6 text-sm">
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block bg-green-600" /> Adult Beginner / Junior HP</div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block bg-amber-600" /> Intermediate</div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block bg-violet-600" /> Cardio Tennis</div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <h3 className="font-bold text-xl text-gray-900">{MONTH_NAMES[month]} {year}</h3>
          <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {days.map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-gray-400 uppercase">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="h-24 border-b border-r border-gray-50" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
            const dots = classDots[day] || [];
            return (
              <div
                key={day}
                className="h-24 border-b border-r border-gray-50 p-2 hover:bg-gray-50 transition-colors"
              >
                <span
                  className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-sm font-semibold mb-1 ${isToday ? 'text-white' : 'text-gray-700'}`}
                  style={isToday ? { backgroundColor: theme.primary } : undefined}
                >
                  {day}
                </span>
                <div className="flex flex-wrap gap-0.5">
                  {dots.map((dot, i) => (
                    <span
                      key={i}
                      className={`w-2 h-2 rounded-full ${dot.colorClass}`}
                      title={dot.label}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── GallerySection ────────────────────────────────────────────────────────────

function GallerySection({ theme }) {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-gray-900">Gallery</h2>
        <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
      </div>

      {/* Masonry-style grid */}
      <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
        {GALLERY_IMAGES.map((src, idx) => (
          <div
            key={idx}
            className="break-inside-avoid cursor-pointer rounded-xl overflow-hidden hover:opacity-90 transition-opacity shadow-sm"
            onClick={() => setLightbox(src)}
          >
            <img
              src={src}
              alt={`Gallery ${idx + 1}`}
              className="w-full object-cover"
              style={{ height: idx % 3 === 0 ? 200 : idx % 3 === 1 ? 160 : 240 }}
            />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-3xl w-full">
            <button
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
              onClick={() => setLightbox(null)}
            >
              <X size={28} />
            </button>
            <img
              src={lightbox.replace('?w=400', '?w=900')}
              alt="Gallery large"
              className="w-full rounded-xl shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ContactSection ────────────────────────────────────────────────────────────

function ContactSection({ config, theme }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const handleSend = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    toast.success('Message sent! We\'ll be in touch shortly.');
    setForm({ name: '', email: '', message: '' });
  };

  const contact = config.contact || {};

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-gray-900">Contact Us</h2>
        <div className="w-12 h-1 rounded mt-2" style={{ backgroundColor: theme.primary }} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-8">
        {/* Contact Info */}
        <div>
          <h3 className="font-bold text-xl text-gray-900 mb-6">Get In Touch</h3>
          <div className="space-y-5">
            {contact.address && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.primaryLight }}>
                  <MapPin size={18} style={{ color: theme.primary }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Address</div>
                  <div className="text-gray-700">{contact.address}</div>
                </div>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.primaryLight }}>
                  <Phone size={18} style={{ color: theme.primary }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Phone</div>
                  <a href={`tel:${contact.phone}`} className="text-gray-700 hover:underline">{contact.phone}</a>
                </div>
              </div>
            )}
            {contact.email && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.primaryLight }}>
                  <Mail size={18} style={{ color: theme.primary }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Email</div>
                  <a href={`mailto:${contact.email}`} className="text-gray-700 hover:underline">{contact.email}</a>
                </div>
              </div>
            )}
            {contact.website && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.primaryLight }}>
                  <Globe size={18} style={{ color: theme.primary }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Website</div>
                  <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:underline">{contact.website}</a>
                </div>
              </div>
            )}
          </div>

          {/* Social */}
          <div className="mt-8">
            <div className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Follow Us</div>
            <div className="flex gap-3">
              {contact.instagram && (
                <a
                  href={`https://instagram.com/${contact.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors hover:opacity-80"
                  style={{ backgroundColor: theme.primary, color: '#ffffff' }}
                >
                  <Hash size={18} />
                </a>
              )}
              {contact.facebook && (
                <a
                  href={`https://facebook.com/${contact.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors hover:opacity-80"
                  style={{ backgroundColor: theme.primary, color: '#ffffff' }}
                >
                  <Share2 size={18} />
                </a>
              )}
              {contact.twitter && (
                <a
                  href={`https://twitter.com/${contact.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors hover:opacity-80"
                  style={{ backgroundColor: theme.primary, color: '#ffffff' }}
                >
                  <AtSign size={18} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div>
          <h3 className="font-bold text-xl text-gray-900 mb-6">Send a Message</h3>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': theme.primary }}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                rows={5}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2 resize-none"
                placeholder="How can we help you?"
              />
            </div>
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: theme.primary }}
            >
              <Send size={16} /> Send Message
            </button>
          </form>
        </div>
      </div>

      {/* Map Placeholder */}
      <div
        className="rounded-2xl flex items-center justify-center h-56"
        style={{ backgroundColor: theme.primaryLight }}
      >
        <div className="text-center">
          <MapPin size={32} className="mx-auto mb-2" style={{ color: theme.primary }} />
          <p className="font-semibold text-gray-700">Map View</p>
          <p className="text-sm text-gray-400">{contact.address}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Join Modal ────────────────────────────────────────────────────────────────

function JoinModal({ theme, config, onClose, navigate }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
        onClick={e => e.stopPropagation()}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold"
          style={{ backgroundColor: theme.primaryLight, color: theme.primary }}
        >
          {getInitials(config.academyName)}
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Join {config.academyName}</h2>
        <p className="text-gray-500 mb-8">
          Create your free account and get access to class enrollment, schedules, and more.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => { navigate('/signup'); onClose(); }}
            className="w-full py-3 rounded-xl font-bold text-white transition-all hover:opacity-90"
            style={{ backgroundColor: theme.primary }}
          >
            Create Account
          </button>
          <button
            onClick={() => { navigate('/signin'); onClose(); }}
            className="w-full py-3 rounded-xl font-bold border-2 transition-all"
            style={{ borderColor: theme.primary, color: theme.primary }}
          >
            Sign In
          </button>
        </div>
        <button onClick={onClose} className="mt-4 text-sm text-gray-400 hover:text-gray-600">
          Maybe later
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const AcademyPortalPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const isDefaultBranding = searchParams.get('branding') === 'default';
  const withinPlatform = pathname.startsWith('/academy/');
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [config, setConfig] = useState(null);
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    const cfg = isPreview ? (getPreviewConfig() || getPortalConfig()) : getPortalConfig();
    // Default branding mode: lock to standard layout + Kynaix theme
    const resolvedConfig = isDefaultBranding
      ? { ...cfg, heroStyle: 'fullscreen', cardStyle: 'elevated', fontPairing: 'modern' }
      : cfg;
    setConfig(resolvedConfig);
    const resolvedTheme = isDefaultBranding
      ? KYNAIX_SPORTS_THEME
      : (ACADEMY_THEMES.find(t => t.id === cfg.themeId) || ACADEMY_THEMES.find(t => t.id === 'navy'));
    setTheme(resolvedTheme);
  }, [isPreview, isDefaultBranding]);

  // Close mobile menu on tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (!config || !theme) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading portal...</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT_PAIRINGS[config.fontPairing || 'modern'], backgroundColor: '#f9fafb', minHeight: '100vh', paddingBottom: (isPreview || isDefaultBranding) ? 64 : 0 }}>
      <PortalNav
        config={config}
        theme={theme}
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        onEnroll={() => setShowJoinModal(true)}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isDefaultBranding={isDefaultBranding}
        withinPlatform={withinPlatform}
      />

      <main>
        {activeTab === 'overview' && (
          <OverviewSection
            config={config}
            theme={theme}
            setActiveTab={handleTabChange}
            onEnroll={() => setShowJoinModal(true)}
            navigate={navigate}
            heroStyle={config.heroStyle || 'fullscreen'}
            cardStyle={config.cardStyle || 'elevated'}
          />
        )}
        {activeTab === 'faculty' && <FacultySection theme={theme} />}
        {activeTab === 'classes' && <ClassesSection theme={theme} navigate={navigate} cardStyle={config.cardStyle || 'elevated'} />}
        {activeTab === 'courts' && <CourtsSection theme={theme} />}
        {activeTab === 'schedule' && <ScheduleSection theme={theme} />}
        {activeTab === 'gallery' && (
          config.features?.gallery
            ? <GallerySection theme={theme} />
            : (
              <div className="max-w-7xl mx-auto px-4 py-24 text-center">
                <p className="text-gray-400">Gallery is not enabled for this portal.</p>
              </div>
            )
        )}
        {activeTab === 'contact' && <ContactSection config={config} theme={theme} />}
      </main>

      <PortalFooter config={config} theme={theme} setActiveTab={handleTabChange} isDefaultBranding={isDefaultBranding} />

      {/* Default Branding Banner */}
      {isDefaultBranding && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-3 shadow-2xl"
          style={{ backgroundColor: '#1e3a8a', borderTop: '2px solid #2563eb' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="px-2.5 py-0.5 rounded-full text-xs font-black flex-shrink-0"
              style={{ backgroundColor: '#ffffff', color: '#1e3a8a' }}
            >
              KS Default
            </div>
            <span className="text-sm hidden sm:block" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Viewing with Default Kynaix Sports branding — this is how your portal looks out of the box
            </span>
            <span className="text-sm sm:hidden" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Default Kynaix Sports branding
            </span>
          </div>
          <button
            onClick={() => window.close()}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all hover:opacity-80 flex-shrink-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
          >
            Close
          </button>
        </div>
      )}

      {/* Preview Banner */}
      {isPreview && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-3 shadow-2xl"
          style={{ backgroundColor: '#111827', borderTop: '2px solid #7c3aed' }}
        >
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#7c3aed', color: '#fff' }}>
              Preview
            </span>
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
              You're previewing a design proposal — changes are not saved yet
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                savePortalConfig(config);
                clearPreviewConfig();
                toast.success('Design applied and saved!');
                setTimeout(() => window.close(), 900);
              }}
              className="px-4 py-1.5 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: '#7c3aed' }}
            >
              Apply & Save
            </button>
            <button
              onClick={() => { clearPreviewConfig(); window.close(); }}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
              style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff' }}
            >
              Exit Preview
            </button>
          </div>
        </div>
      )}

      {showJoinModal && (
        <JoinModal
          theme={theme}
          config={config}
          onClose={() => setShowJoinModal(false)}
          navigate={navigate}
        />
      )}
    </div>
  );
};

export default AcademyPortalPage;
